/**
 * ACTIVITY — the whole record, plus every correction made to it.
 *
 * Two sections, and they answer two different questions. The feed answers "what
 * has been going on"; the corrections log answers "show me every correction",
 * which nothing could ask before it existed — an edit used to be visible only
 * inside the record it changed.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { CorrectionEntry } from '../../../../../api/activity-client';
import { FormModal } from '../../../../modal/form-modal';
import { RecordDetail } from '../../../../activity/record-detail';
import {
  palette,
  radius,
  spacing,
  typography,
} from '../../../../../style-utils/theme';
import { PanelShell, ChartShellState } from '../charts/chart-shell';
import { PanelToggle } from '../charts/chart.styled';
import { PanelGrid } from '../dashboard-screen.styled';
import { ActivityPanel } from '../panels/activity-panel';
import { format } from '../charts/chart-theme';
import { useClock } from '../../../../../context/clock-context';
import { ClockNote } from '../../../../clock-note/clock-note';
import { openRecordLabel, sourceNoun } from '../../../../../function-utils/record-label';
import { BOOKS, BOOK_LABELS, Book, DashboardFilter } from '../state/filter-model';
import { FilterAction } from '../state/filter-reducer';
import { DashboardData } from '../data/dashboard-data';

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.xs} 4px;
  border-top: 1px solid ${palette.line};

  &:first-of-type {
    border-top: none;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing.xs};
  flex-wrap: wrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.ink};
`;

const When = styled.span`
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  font-variant-numeric: tabular-nums;
`;

const Change = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};

  code {
    padding: 0 4px;
    border-radius: ${radius.xs};
    background: ${palette.surfaceMuted};
    font-family: ${typography.mono};
    overflow-wrap: anywhere;
  }
`;

const BookChips = styled.div`
  display: flex;
  gap: ${spacing.half};
  flex-wrap: wrap;
`;

export interface ActivityTabProps {
  filter: DashboardFilter;
  dispatch: (action: FilterAction, opts?: { replace?: boolean }) => void;
  data: DashboardData;
}

/** One correction, rendered. A change whose previous value could not be
 *  decrypted says so — it never shows the raw token, and never pretends the
 *  field was empty. */
const CorrectionRow: React.FC<{
  entry: CorrectionEntry;
  onOpen: (source: string, id: string) => void;
  /* Threaded rather than read from a context: this row is a leaf and the zone
     is a property of the ANSWER, so it travels with the data that carries the
     instant it formats. */
  tz: string;
}> = ({ entry, onOpen, tz }) => (
  <Item>
    <Head>
      <span>
        {format.humanise(entry.recordSource)} #{entry.recordId} · v{entry.version}
        {entry.actor?.name ? ` · by ${entry.actor.name}` : ''}
      </span>
      <When>{format.instant(entry.at, tz)}</When>
    </Head>
    {entry.changes == null ? (
      /* The trail outlives the record on purpose: there is a previous state and
         nothing left to compare it against. */
      <Change>
        <em>The corrected record has since been deleted, so there is nothing to compare.</em>
      </Change>
    ) : (
      entry.changes.map(change => (
        <Change key={change.field}>
          <strong>{format.humanise(change.field)}</strong>
          <code>
            {change.previousReadable
              ? change.previous || '(empty)'
              : 'previous value unavailable'}
          </code>
          <span aria-hidden="true">→</span>
          <code>
            {change.currentReadable
              ? change.current || '(empty)'
              : 'current value unavailable'}
          </code>
        </Change>
      ))
    )}
    {/* WP-36 / MR-14. "Open the record" was already better than the bare
        "Open" on the other two surfaces, but it is still the same string on
        every row of the corrections trail — and this list is precisely where a
        reader is comparing one record against another. The visible text is
        unchanged; only the name a screen reader reads gains the row. */}
    <PanelToggle
      type="button"
      aria-label={openRecordLabel(sourceNoun(entry.recordSource), [
        `#${entry.recordId}`,
        `version ${entry.version}`,
        format.instant(entry.at, tz),
      ])}
      onClick={() => onOpen(entry.recordSource, entry.recordId)}
    >
      Open the record
    </PanelToggle>
  </Item>
);

export const ActivityTab: React.FC<ActivityTabProps> = ({
  filter,
  dispatch,
  data,
}) => {
  const [open, setOpen] = useState<{ source: string; id: string } | null>(null);
  /* ONE CLOCK FOR THE TAB — the feed answer's own (`tz`, R-T12): the reader's
     for an owner or a manager, the building's for a concierge. The corrections
     list has no clock of its own and is printed on the same one. */
  const clock = useClock({ buildingId: filter.buildingId, response: data.activity.data });
  const tz = clock.zone;

  const feedState: ChartShellState = {
    loading: data.activity.loading,
    stale: data.activity.stale,
    error: data.activity.error,
    onRetry: data.activity.retry,
  };

  const corrections = data.corrections.data?.corrections || [];

  return (
    <>
      <PanelGrid data-testid="panel-grid">
        <ActivityPanel
          state={feedState}
          data={data.activity.data}
          filter={filter}
          dispatch={dispatch}
          onOpenRecord={(source, id) => setOpen({ source, id: String(id) })}
        />

        <PanelShell
          id="corrections"
          title="Every correction"
          wide
          state={{
            loading: data.corrections.loading,
            stale: data.corrections.stale,
            error: data.corrections.error,
            onRetry: data.corrections.retry,
          }}
          caveat="Every edit made to a record in this organisation, newest first. The feed above carries each correction as a row in the timeline; this answers the other question — show me all of them."
          emptyReason={
            data.corrections.data && !corrections.length
              ? {
                  title: 'Nothing has been corrected',
                  text: 'No record in this book has been edited yet. That is the good answer.',
                }
              : null
          }
          controls={
            <BookChips>
              {BOOKS.map(book => (
                <PanelToggle
                  key={book}
                  type="button"
                  active={filter.book === book}
                  aria-pressed={filter.book === book}
                  data-testid={`corrections-book-${book}`}
                  onClick={() => dispatch({ type: 'setBook', book: book as Book })}
                >
                  {BOOK_LABELS[book]}
                </PanelToggle>
              ))}
            </BookChips>
          }
        >
          <ClockNote clock={clock} />
          <List data-testid="corrections-list">
            {corrections.map(entry => (
              <CorrectionRow
                key={entry.id}
                entry={entry}
                tz={tz}
                onOpen={(source, id) => setOpen({ source, id })}
              />
            ))}
          </List>
          {data.corrections.data?.hasMore ? (
            <PanelToggle
              type="button"
              onClick={() => dispatch({ type: 'setPage', page: filter.page + 1 })}
            >
              Show more
            </PanelToggle>
          ) : null}
        </PanelShell>
      </PanelGrid>

      {open ? (
        <FormModal wide title="Record" onClose={() => setOpen(null)}>
          <RecordDetail
            source={open.source}
            id={open.id}
            onClose={() => setOpen(null)}
            // An out taken from the record is a new row in this feed.
            onChanged={data.activity.retry}
          />
        </FormModal>
      ) : null}
    </>
  );
};
